import { useRef, useState, useEffect } from 'react';
import { NodeGroup } from '../../../types/groupTypes';
import { hexToRgba } from '../../../utils/groupColors';
import { Trash2, Palette, Sparkles, AlertCircle, Tag, Package } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { useFunnelStages } from '../../../hooks/useFunnelStages';

interface GroupNodeProps {
  group: NodeGroup;
  viewport: { x: number; y: number; zoom: number };
  onColorChange: (color: string) => void;
  onInstructionChange: (instruction: string) => void;
  onLeadStatusChange: (leadStatus: string) => void;
  onProductKeysChange: (productKeys: string[]) => void;
  onDelete: () => void;
  availableProducts?: Array<{ id: string; product_key: string; product_name: string }>;
}

interface GroupNodeDisplayProps {
  group: NodeGroup;
  viewport: { x: number; y: number; zoom: number };
}

export function GroupNode({ group, viewport }: GroupNodeDisplayProps) {
  const transform = `translate(${group.position.x * viewport.zoom + viewport.x}px, ${group.position.y * viewport.zoom + viewport.y
    }px) scale(${viewport.zoom})`;

  const borderColor = group.color;
  const backgroundColor = hexToRgba(group.color, 0.08);
  
  // Warning si falta instrucción
  const hasWarning = !group.instruction;

  return (
    <div
      className="group-container absolute"
      style={{
        transform,
        transformOrigin: '0 0',
        width: group.dimensions.width,
        height: group.dimensions.height,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    >
      {/* Rectángulo del grupo */}
      <div
        className={`absolute inset-0 rounded-lg ${hasWarning ? 'animate-pulse' : ''}`}
        style={{
          border: `${hasWarning ? '3' : '2'}px ${hasWarning ? 'dashed' : 'solid'} ${hasWarning ? '#f59e0b' : borderColor}`,
          backgroundColor: hasWarning ? 'rgba(245, 158, 11, 0.05)' : backgroundColor,
        }}
      />

      {/* Etiqueta del grupo - arriba a la izquierda */}
      <div
        className="absolute -top-3 left-4 px-3 py-1 bg-white rounded-md shadow-md border-2 flex items-center gap-2"
        style={{
          pointerEvents: 'none',
          borderColor: hasWarning ? '#f59e0b' : borderColor,
          zIndex: 1001,
        }}
      >
        <span className="text-sm font-semibold select-none" style={{ color: hasWarning ? '#f59e0b' : borderColor }}>
          {group.number}. {group.nodeIds.length === 1 ? 'Mensaje' : 'Mensajes'}
        </span>
        {group.productKeys && group.productKeys.length > 0 && (
          <span 
            className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white font-medium"
            title={`${group.productKeys.length} producto(s) asociado(s)`}
          >
            📦 {group.productKeys.length}
          </span>
        )}
        {group.leadStatus && (
          <span 
            className="text-xs px-2 py-0.5 rounded-full bg-purple-500 text-white font-medium"
            title={`Estado del lead: ${group.leadStatus}`}
          >
            → {group.leadStatus}
          </span>
        )}
        {hasWarning && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500 text-white font-medium animate-pulse">
            ⚠ Sin instrucción
          </span>
        )}
        {!hasWarning && group.instruction && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white font-medium">
            ✓
          </span>
        )}
      </div>
    </div>
  );
}

// Botones de control renderizados fuera del contenedor del grupo
export function GroupControls({ group, viewport, onColorChange, onInstructionChange, onLeadStatusChange, onProductKeysChange, onDelete, availableProducts = [] }: GroupNodeProps) {
  console.log('🎯 GroupControls renderizado con productos:', availableProducts);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const [showInstructionPopover, setShowInstructionPopover] = useState(false);
  const [showLeadStatusPopover, setShowLeadStatusPopover] = useState(false);
  const [showProductsPopover, setShowProductsPopover] = useState(false);
  const [localInstruction, setLocalInstruction] = useState(group.instruction || '');
  const [localLeadStatus, setLocalLeadStatus] = useState(group.leadStatus || '');
  const [selectedProducts, setSelectedProducts] = useState<string[]>(group.productKeys || []);
  
  // Cargar etapas disponibles
  const { stages, loading: loadingStages } = useFunnelStages();

  // No abrir el popover automáticamente, solo mostrar el botón
  // El usuario decide cuándo agregar la instrucción
  useEffect(() => {
    // Popover se abre manualmente al hacer clic
  }, []);

  const transform = `translate(${group.position.x * viewport.zoom + viewport.x}px, ${group.position.y * viewport.zoom + viewport.y
    }px) scale(${viewport.zoom})`;

  const handleSaveInstruction = () => {
    if (localInstruction.trim()) {
      onInstructionChange(localInstruction);
      setShowInstructionPopover(false);
    }
  };

  const handleSaveLeadStatus = () => {
    if (localLeadStatus.trim()) {
      onLeadStatusChange(localLeadStatus);
      setShowLeadStatusPopover(false);
    }
  };

  const handleToggleProduct = (productKey: string) => {
    const newSelection = selectedProducts.includes(productKey)
      ? selectedProducts.filter(k => k !== productKey)
      : [...selectedProducts, productKey];
    setSelectedProducts(newSelection);
  };

  const handleSaveProducts = () => {
    onProductKeysChange(selectedProducts);
    setShowProductsPopover(false);
  };

  return (
    <div
      className="absolute"
      style={{
        transform,
        transformOrigin: '0 0',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {/* Botones de control - arriba del grupo */}
      <div
        className="absolute flex flex-row gap-2"
        style={{
          top: '-60px',
          left: '0px',
          pointerEvents: 'all',
          zIndex: 9999,
        }}
      >
        {/* Botón de instrucción IA */}
        <div className="relative group">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowInstructionPopover(!showInstructionPopover);
            }}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all shadow-lg cursor-pointer ${group.instruction
              ? 'bg-purple-600 hover:bg-purple-700'
              : 'bg-yellow-400 hover:bg-yellow-500 animate-pulse'
              }`}
          >
            {group.instruction ? (
              <Sparkles className="w-5 h-5 text-white" />
            ) : (
              <AlertCircle className="w-5 h-5 text-white" />
            )}
          </button>

          {/* Tooltip */}
          <div className="absolute top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[10000]">
            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              {group.instruction ? 'Editar instrucción IA' : '⚠️ Instrucción requerida'}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>

          {/* Modal de instrucción */}
          {showInstructionPopover && (
            <div
              className="absolute w-96"
              style={{
                top: `${group.dimensions.height + 80}px`,
                left: '0px',
                zIndex: 99999,
                pointerEvents: 'all'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal estilo nodo de mensaje */}
              <div
                className="bg-white rounded-xl shadow-2xl border-2 border-purple-500/30"
                style={{
                  animation: 'scaleIn 0.2s ease-out',
                }}
              >
                {/* Header del modal */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${group.instruction ? 'bg-purple-100' : 'bg-yellow-100'
                      }`}>
                      {group.instruction ? (
                        <Sparkles className="w-5 h-5 text-purple-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900">
                        ¿Cuándo enviar estos mensajes?
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Define la condición para usar este flujo
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contenido del modal */}
                <div className="p-4 space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block font-medium">
                      Instrucción para la IA: <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={localInstruction}
                      onChange={(e) => setLocalInstruction(e.target.value)}
                      placeholder="Ej: Cuando el usuario pregunte por precios, horarios o ubicación..."
                      className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:outline-none focus:ring-2 resize-none transition-all ${!localInstruction.trim()
                        ? 'border-red-300 focus:ring-red-500 bg-red-50/30'
                        : 'border-gray-300 focus:ring-purple-500'
                        }`}
                      rows={5}
                      autoFocus
                    />
                  </div>

                  {/* Info o error */}
                  {!localInstruction.trim() ? (
                    <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">
                        Este campo es obligatorio para que la IA sepa cuándo usar este flujo
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{localInstruction.length} caracteres</span>
                      <span className="text-green-600">✓ Listo para guardar</span>
                    </div>
                  )}
                </div>

                {/* Footer con botones */}
                <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowInstructionPopover(false)}
                    className="h-9 text-sm"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveInstruction}
                    disabled={!localInstruction.trim()}
                    className={`h-9 text-sm ${!localInstruction.trim() ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    {group.instruction ? 'Actualizar' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botón de estado de lead */}
        <div className="relative group">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowLeadStatusPopover(!showLeadStatusPopover);
            }}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all shadow-lg cursor-pointer ${group.leadStatus
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-gray-200 hover:bg-gray-300'
              }`}
          >
            <Tag className={`w-5 h-5 ${group.leadStatus ? 'text-white' : 'text-gray-600'}`} />
          </button>

          {/* Tooltip */}
          <div className="absolute top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[10000]">
            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              {group.leadStatus ? 'Editar estado del lead' : 'Agregar estado del lead'}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>

          {/* Modal de estado */}
          {showLeadStatusPopover && (
            <div
              className="absolute w-80"
              style={{
                top: `${group.dimensions.height + 80}px`,
                left: '0px',
                zIndex: 99999,
                pointerEvents: 'all'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="bg-white rounded-xl shadow-2xl border-2 border-blue-500/30"
                style={{
                  animation: 'scaleIn 0.2s ease-out',
                }}
              >
                {/* Header del modal */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${group.leadStatus ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                      <Tag className={`w-5 h-5 ${group.leadStatus ? 'text-blue-600' : 'text-gray-600'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900">
                        Estado del Lead
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Define el estado del cliente cuando se envíe este mensaje
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contenido del modal */}
                <div className="p-4 space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block font-medium">
                      Selecciona la etapa:
                    </label>
                    {loadingStages ? (
                      <div className="text-sm text-gray-500 py-2">Cargando etapas...</div>
                    ) : stages.length === 0 ? (
                      <div className="text-sm text-gray-500 py-2">
                        No hay etapas disponibles. Crea etapas en el Inbox.
                      </div>
                    ) : (
                      <select
                        value={localLeadStatus}
                        onChange={(e) => setLocalLeadStatus(e.target.value)}
                        className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        autoFocus
                      >
                        <option value="">Selecciona una etapa</option>
                        {stages.map((stage) => (
                          <option key={stage.id} value={stage.name}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {localLeadStatus.trim() && (
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Etapa seleccionada: {localLeadStatus}</span>
                      <span className="text-green-600">✓ Listo para guardar</span>
                    </div>
                  )}
                </div>

                {/* Footer con botones */}
                <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowLeadStatusPopover(false)}
                    className="h-9 text-sm"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveLeadStatus}
                    disabled={!localLeadStatus.trim()}
                    className={`h-9 text-sm ${!localLeadStatus.trim() ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    {group.leadStatus ? 'Actualizar' : 'Guardar'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botón de productos */}
        <div className="relative group">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowProductsPopover(!showProductsPopover);
            }}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all shadow-lg cursor-pointer ${
              group.productKeys && group.productKeys.length > 0
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            <Package className={`w-5 h-5 ${group.productKeys && group.productKeys.length > 0 ? 'text-white' : 'text-gray-600'}`} />
          </button>

          {/* Tooltip */}
          <div className="absolute top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[10000]">
            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              {group.productKeys && group.productKeys.length > 0 ? 'Editar productos asociados' : 'Asociar productos'}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>

          {/* Modal de productos */}
          {showProductsPopover && (
            <div
              className="absolute w-[420px]"
              style={{
                top: `${group.dimensions.height + 80}px`,
                left: '0px',
                zIndex: 99999,
                pointerEvents: 'all'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="bg-white rounded-xl shadow-2xl border-2 border-blue-500/30"
                style={{
                  animation: 'scaleIn 0.2s ease-out',
                }}
              >
                {/* Header del modal */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900">
                        Productos Asociados
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Selecciona los productos relacionados con este flujo
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contenido del modal */}
                <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                  {availableProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No hay productos disponibles</p>
                      <p className="text-xs mt-1">Crea productos en la configuración del agente</p>
                    </div>
                  ) : (
                    availableProducts.map((product) => {
                      const isSelected = selectedProducts.includes(product.product_key);
                      return (
                        <button
                          key={product.id}
                          onClick={() => handleToggleProduct(product.product_key)}
                          className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                            }`}>
                              {isSelected && <span className="text-white text-xs">✓</span>}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900">{product.product_name}</span>
                                {isSelected && (
                                  <span className="text-xs text-blue-600 font-medium">Seleccionado</span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">Key: {product.product_key}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer con botones */}
                <div className="p-4 border-t border-gray-200 flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedProducts(group.productKeys || []);
                      setShowProductsPopover(false);
                    }}
                    className="h-9 text-sm"
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSaveProducts}
                    className="h-9 text-sm"
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Selector de color */}
        <div className="relative group">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              colorInputRef.current?.click();
            }}
            className="w-10 h-10 rounded-lg bg-white hover:bg-gray-50 flex items-center justify-center shadow-lg border-2 border-gray-200 transition-colors cursor-pointer"
          >
            <Palette className="w-5 h-5 text-gray-600" />
          </button>

          {/* Tooltip */}
          <div className="absolute top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[10000]">
            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              Cambiar color del grupo
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>

          <input
            ref={colorInputRef}
            type="color"
            value={group.color}
            onChange={(e) => {
              e.stopPropagation();
              onColorChange(e.target.value);
            }}
            className="absolute opacity-0 w-0 h-0 pointer-events-none"
          />
        </div>

        {/* Botón eliminar */}
        <div className="relative group">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            className="w-10 h-10 rounded-lg bg-white hover:bg-red-50 flex items-center justify-center shadow-lg border-2 border-red-200 transition-colors cursor-pointer"
          >
            <Trash2 className="w-5 h-5 text-red-500" />
          </button>

          {/* Tooltip */}
          <div className="absolute top-14 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[10000]">
            <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
              Eliminar grupo (los nodos permanecen)
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
